import { useEffect, useMemo, useRef, useState } from 'react';
import { analyzeImageFile, markPossibleDuplicates, pickBestPhotos } from '../lib/photoScoring';
import { getLocalAssetKey, localAssetStorageMode, saveLocalAsset } from '../lib/localAssetStore';
import { loadLastProject, saveProject } from '../lib/projectStorage';
import { registerPhotoSessionUrls } from '../lib/photoSession';
import { usePhotoPreviewUrls } from '../lib/usePhotoPreviewUrls';
import type { CalendarPhoto, CalendarProject } from '../types/calendarProject';
import { Button } from '../components/ui/Button';
import { Logo } from '../components/ui/Logo';

const progressSteps = ['Produkt', 'Fotky', 'Návrh', 'Editor', 'Preflight'];
const qualityLabels = { good: 'Good', warning: 'Warning', bad: 'Bad' };
const orientationLabels = { landscape: 'na šířku', portrait: 'na výšku', square: 'čtverec' };

function navigateTo(path: string) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function countByQuality(photos: CalendarPhoto[], status: CalendarPhoto['qualityStatus']) {
  return photos.filter((photo) => photo.qualityStatus === status).length;
}

function formatSize(sizeBytes: number) {
  if (sizeBytes > 1024 * 1024) return `${(sizeBytes / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(sizeBytes / 1024))} kB`;
}

export function UploadFlow() {
  const [project, setProject] = useState<CalendarProject | null>(null);
  const [photos, setPhotos] = useState<CalendarPhoto[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [notice, setNotice] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const previewProject = useMemo(() => (project ? { ...project, photos } : null), [project, photos]);
  const previewUrls = usePhotoPreviewUrls(previewProject);

  useEffect(() => {
    const lastProject = loadLastProject();
    setProject(lastProject);
    setPhotos(lastProject?.photos ?? []);
  }, []);

  const selectedPhotos = useMemo(() => photos.filter((photo) => photo.selectedForDraft), [photos]);
  const hasHeicWarning = photos.some((photo) => photo.issues.includes('Nepodporovaný formát náhledu'));

  const updateSelection = (nextPhotos: CalendarPhoto[]) => {
    const bestIds = new Set(pickBestPhotos(nextPhotos, 13).map((photo) => photo.id));
    return nextPhotos.map((photo) => ({ ...photo, selectedForDraft: bestIds.has(photo.id) }));
  };

  const handleFiles = async (fileList: FileList | File[]) => {
    if (!project) return;

    const files = Array.from(fileList).filter((file) => file.type.startsWith('image/') || /\.(heic|heif)$/i.test(file.name));
    if (!files.length) return;

    setIsAnalyzing(true);
    setNotice('');

    try {
      const analyzedPhotos = await Promise.all(files.map((file) => analyzeImageFile(file)));
      const photosWithLocalAssets = await Promise.all(analyzedPhotos.map(async (photo, index) => {
        try {
          await saveLocalAsset(project.id, photo.id, files[index]);
          return {
            ...photo,
            localAssetKey: getLocalAssetKey(project.id, photo.id),
            hasLocalAsset: true,
            assetStorage: localAssetStorageMode,
            uploadStatus: 'local-only',
            originalFileName: photo.fileName,
            productionReady: false,
          } satisfies CalendarPhoto;
        } catch {
          return {
            ...photo,
            localAssetKey: getLocalAssetKey(project.id, photo.id),
            hasLocalAsset: false,
            assetStorage: localAssetStorageMode,
            uploadStatus: 'failed',
            originalFileName: photo.fileName,
            productionReady: false,
          } satisfies CalendarPhoto;
        }
      }));
      registerPhotoSessionUrls(photosWithLocalAssets.map((photo) => photo.objectUrl));
      setPhotos((currentPhotos) => updateSelection(markPossibleDuplicates([...currentPhotos, ...photosWithLocalAssets])));
      setNotice('Vybrali jsme 13 nejlepších fotek pro první návrh a originály uložili lokálně do tohoto prohlížeče.');
    } catch {
      setNotice('Některé fotky se nepodařilo zpracovat. Zkus je nahrát znovu nebo vyber jiný formát.');
    } finally {
      setIsAnalyzing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const togglePhoto = (photoId: string) => {
    setPhotos((currentPhotos) => currentPhotos.map((photo) => (
      photo.id === photoId ? { ...photo, selectedForDraft: !photo.selectedForDraft } : photo
    )));
  };

  const handleCreateDraft = () => {
    if (!project) return;

    saveProject({
      ...project,
      photos,
      updatedAt: new Date().toISOString(),
    });
    navigateTo('/draft');
  };

  if (!project) {
    return (
      <main className="upload-shell">
        <div className="upload-card upload-empty-state">
          <Logo />
          <h1>Nejdřív vyber typ kalendáře</h1>
          <p>Upload fotek navazuje na lokální návrh projektu ze start flow.</p>
          <Button href="/start">Začít výběrem</Button>
        </div>
      </main>
    );
  }

  return (
    <main className="upload-shell">
      <div className="upload-card">
        <header className="upload-header">
          <Logo />
          <div className="upload-header__links">
            <a href="/projects" className="upload-header__link">Moje projekty</a>
            <a href="/start" className="upload-header__link">Zpět na výběr</a>
          </div>
        </header>

        <nav className="upload-progress" aria-label="Kroky Magic Flow">
          {progressSteps.map((step, index) => (
            <span key={step} className={index === 1 ? 'is-active' : ''}><small>0{index + 1}</small>{step}</span>
          ))}
        </nav>

        <section className="upload-hero" aria-labelledby="upload-title">
          <p className="eyebrow">Magic Flow</p>
          <h1 id="upload-title">Nahraj fotky</h1>
          <p>Vyber 13–60 fotek. Chytrý výběr najde nejlepší kandidáty pro titulku a 12 měsíců.</p>
        </section>

        <div className="upload-layout">
          <div className="upload-main">
            <label
              className={isDragging ? 'upload-dropzone is-dragging' : 'upload-dropzone'}
              onDragOver={(event) => { event.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(event) => {
                event.preventDefault();
                setIsDragging(false);
                void handleFiles(event.dataTransfer.files);
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                onChange={(event) => event.target.files && void handleFiles(event.target.files)}
              />
              <strong>Přetáhni fotky sem</strong>
              <span>nebo vyber z počítače / galerie</span>
              <em>Vybrat fotky</em>
            </label>

            <div className="upload-storage-box">
              <strong>Lokální uložení fotek</strong>
              <p>V této MVP verzi zůstávají fotky pouze v tomto prohlížeči. Při vymazání dat prohlížeče mohou zmizet. Do výroby půjde později bezpečný cloud upload originálů.</p>
            </div>
            <p className="upload-note">Nic se nikam neodesílá. Nejde o produkční cloud upload, jen o lokální MVP storage.</p>
            {hasHeicWarning && <p className="upload-warning">HEIC/HEIF soubory mohou být v tomto prohlížeči bez náhledu. Metadata v seznamu zůstanou zachovaná.</p>}
            {isAnalyzing && <div className="upload-status">Analyzuji fotky…</div>}
            {notice && <div className="upload-status upload-status--done">{notice}</div>}

            <section className="upload-stats" aria-label="Souhrn fotek">
              <div><span>Nahráno</span><strong>{photos.length}</strong></div>
              <div><span>Good</span><strong>{countByQuality(photos, 'good')}</strong></div>
              <div><span>Warning</span><strong>{countByQuality(photos, 'warning')}</strong></div>
              <div><span>Bad</span><strong>{countByQuality(photos, 'bad')}</strong></div>
            </section>

            {photos.length > 0 && (
              <section className="photo-grid" aria-label="Nahrané fotky">
                {photos.map((photo) => (
                  <article key={photo.id} className={photo.selectedForDraft ? 'photo-card is-selected' : 'photo-card'}>
                    <div className="photo-card__preview">
                      {photo.width && photo.height && previewUrls[photo.id] && !photo.issues.includes('Nepodporovaný formát náhledu') ? (
                        <img src={previewUrls[photo.id]} alt={photo.fileName} />
                      ) : (
                        <div className="photo-card__no-preview">Bez náhledu</div>
                      )}
                      <span className="photo-score">{photo.score}</span>
                    </div>
                    <div className="photo-card__body">
                      <div className="photo-card__topline">
                        <strong title={photo.fileName}>{photo.fileName}</strong>
                        <span className={`quality-badge quality-badge--${photo.qualityStatus}`}>{qualityLabels[photo.qualityStatus]}</span>
                      </div>
                      <p>{orientationLabels[photo.orientation]} · {photo.width || '—'} × {photo.height || '—'} px · {formatSize(photo.sizeBytes)}</p>
                      {photo.issues.length > 0 && <small>{photo.issues.join(' · ')}</small>}
                      <label className="photo-toggle">
                        <input type="checkbox" checked={photo.selectedForDraft} onChange={() => togglePhoto(photo.id)} />
                        Použít v návrhu
                      </label>
                    </div>
                  </article>
                ))}
              </section>
            )}
          </div>

          <aside className="smart-panel" aria-label="Smart výběr">
            <p className="eyebrow">Smart výběr</p>
            <h2>{selectedPhotos.length} vybraných</h2>
            <p>Titulka + 12 měsíců</p>
            {selectedPhotos.length > 0 ? (
              <ol>
                {selectedPhotos.slice(0, 13).map((photo, index) => <li key={photo.id}><span>{index + 1}</span>{photo.fileName}</li>)}
              </ol>
            ) : (
              <p className="smart-panel__empty">Nahraj fotky a vybereme nejlepší kandidáty.</p>
            )}
            <button type="button" className="upload-primary" onClick={handleCreateDraft} disabled={selectedPhotos.length === 0}>Vytvořit první návrh</button>
            <Button href="/start" variant="secondary">Zpět na výběr</Button>
          </aside>
        </div>
      </div>
    </main>
  );
}
